import { api } from '../../lib/api-client';

export type PartsRequestStatus =
  'REQUESTED' | 'RESERVED' | 'ISSUED' | 'PARTIALLY_ISSUED' | 'BACKORDER' | 'CANCELLED';

export interface SkuBalance {
  locationUid: string;
  locationName: string;
  onHand: string;
  reserved: string;
  available: string;
  /** AutoCount's figure — the authority (ADR 0006) — and when it was read. */
  autocountQty: string | null;
  autocountReadAt: string | null;
  /** DMS minus AutoCount. Non-zero means somebody should look. */
  drift: string | null;
}

export interface Sku {
  uid: string;
  skuCode: string;
  name: string;
  category: string | null;
  uom: string;
  barcode: string | null;
  sellPrice: string;
  movingAverageCost: string;
  reorderLevel: string;
  totalOnHand: string;
  totalAvailable: string;
  isLow: boolean;
  balances: SkuBalance[];
}

export interface SkuPage {
  items: Sku[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PartsRequestLine {
  uid: string;
  skuUid: string;
  skuCode: string;
  skuName: string;
  qtyRequested: string;
  qtyIssued: string;
  qtyReturned: string;
  available: string | null;
  autocountQty: string | null;
  autocountReadAt: string | null;
}

export interface PartsRequest {
  uid: string;
  requestNo: string;
  status: PartsRequestStatus;
  jobCardUid: string | null;
  jobNo: string | null;
  plateNo: string | null;
  requestedAt: string;
  issuedAt: string | null;
  note: string | null;
  /** Where the parts were put aside; the issue picker starts here. */
  locationUid: string | null;
  locationName: string | null;
  lines: PartsRequestLine[];
  branchCode: string | null;
}

export interface PartsRequestPage {
  items: PartsRequest[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface DriftRow {
  skuCode: string;
  skuName: string;
  locationName: string;
  dmsQty: string;
  autocountQty: string | null;
  drift: string;
}

export interface LedgerDriftRow {
  skuCode: string;
  locationName: string;
  ledgerQty: string;
  cachedQty: string;
  drift: string;
}

export interface ReconciliationReport {
  ranAt: string;
  autocountReadAt: string | null;
  /** The largest differences first, capped by the server. */
  autocountDrift: DriftRow[];
  /** How many rows disagree in all. */
  autocountDriftTotal: number;
  unknownToDms: string[];
  /** Should always be empty. A row here is a bug, not a business fact. */
  ledgerDrift: LedgerDriftRow[];
  overrides: number;
}

export function listSkus(params: { q?: string; lowStock?: boolean; cursor?: string }): Promise<SkuPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.lowStock === true) search.set('lowStock', 'true');
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<SkuPage>(`/parts/skus${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getSku(uid: string): Promise<Sku> {
  return api<Sku>(`/parts/skus/${uid}`);
}

export function listPartsRequests(params: {
  status?: PartsRequestStatus;
  open?: boolean;
  jobCardUid?: string;
  cursor?: string;
}): Promise<PartsRequestPage> {
  const search = new URLSearchParams();
  if (params.status !== undefined) search.set('status', params.status);
  if (params.open === true) search.set('open', 'true');
  if (params.jobCardUid !== undefined) search.set('jobCardUid', params.jobCardUid);
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<PartsRequestPage>(`/parts/requests${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getPartsRequest(uid: string): Promise<PartsRequest> {
  return api<PartsRequest>(`/parts/requests/${uid}`);
}

export function createPartsRequest(input: {
  jobCardUid: string;
  locationUid?: string;
  lines: { skuUid: string; qty: string }[];
  note?: string;
}): Promise<PartsRequest> {
  return api<PartsRequest>('/parts/requests', { method: 'POST', body: input });
}

export function issueParts(
  uid: string,
  input: {
    locationUid?: string;
    lines: { lineUid: string; qty: string; acknowledgeNegative?: boolean }[];
  },
): Promise<PartsRequest> {
  return api<PartsRequest>(`/parts/requests/${uid}/transitions/issue`, {
    method: 'POST',
    body: input,
  });
}

export function returnParts(
  uid: string,
  input: { locationUid?: string; lines: { lineUid: string; qty: string }[]; reason: string },
): Promise<PartsRequest> {
  return api<PartsRequest>(`/parts/requests/${uid}/transitions/return`, {
    method: 'POST',
    body: input,
  });
}

export function adjustStock(input: {
  skuUid: string;
  locationUid: string;
  countedQty: string;
  reason: string;
}): Promise<{ before: string; after: string; delta: string }> {
  return api<{ before: string; after: string; delta: string }>('/parts/adjustments', {
    method: 'POST',
    body: input,
  });
}

export function runReconciliation(): Promise<ReconciliationReport> {
  return api<ReconciliationReport>('/parts/reconciliation');
}

export type CounterOrderStatus =
  'DRAFT' | 'PENDING_APPROVAL' | 'AWAITING_PAYMENT' | 'READY_TO_COLLECT' | 'COLLECTED' | 'RETURNED' | 'CANCELLED';

export interface CounterOrderLine {
  uid: string; skuUid: string; skuCode: string; skuName: string;
  qty: string; unitPrice: string; discountAmount: string; lineTotal: string;
}

/** Money is a decimal STRING on this boundary. */
export interface CounterOrder {
  uid: string; orderNo: string; status: CounterOrderStatus;
  customerUid: string | null; customerName: string | null;
  invoiceUid: string | null; invoiceNo: string | null;
  subtotal: string; discountTotal: string; grandTotal: string; discountPercent: string;
  lines: CounterOrderLine[];
  createdAt: string; submittedAt: string | null; collectedAt: string | null; cancelReason: string | null;
}

export interface CounterOrderPage { items: CounterOrder[]; nextCursor: string | null; hasMore: boolean }

const CO = '/parts/counter-orders';

export const createCounterOrder = (input: { customerUid?: string; locationUid?: string }): Promise<CounterOrder> =>
  api<CounterOrder>(CO, { method: 'POST', body: input });
export const getCounterOrder = (uid: string): Promise<CounterOrder> => api<CounterOrder>(`${CO}/${uid}`);
export const listCounterOrders = (status: CounterOrderStatus, page: { cursor?: string } = {}): Promise<CounterOrderPage> =>
  api<CounterOrderPage>(`${CO}?status=${status}${page.cursor !== undefined ? `&cursor=${encodeURIComponent(page.cursor)}` : ''}`);
export const addCounterLine = (uid: string, input: { code: string; qty: string }): Promise<CounterOrder> =>
  api<CounterOrder>(`${CO}/${uid}/lines`, { method: 'POST', body: input });
export const changeCounterLine = (
  uid: string, lineUid: string, input: { qty?: string; discountAmount?: string },
): Promise<CounterOrder> => api<CounterOrder>(`${CO}/${uid}/lines/${lineUid}`, { method: 'PATCH', body: input });
export const removeCounterLine = (uid: string, lineUid: string): Promise<CounterOrder> =>
  api<CounterOrder>(`${CO}/${uid}/lines/${lineUid}`, { method: 'DELETE' });
export const submitCounterOrder = (uid: string): Promise<CounterOrder> =>
  api<CounterOrder>(`${CO}/${uid}/transitions/submit`, { method: 'POST', body: {} });
export const cancelCounterOrder = (uid: string, reason: string): Promise<CounterOrder> =>
  api<CounterOrder>(`${CO}/${uid}/transitions/cancel`, { method: 'POST', body: { reason } });
export const lookupCounterOrder = (code: string): Promise<CounterOrder> =>
  api<CounterOrder>(`${CO}/lookup?code=${encodeURIComponent(code)}`);
/** `idempotencyKey` is required for the same reason takePayment's is. */
export const collectCounterOrder = (
  uid: string, input: { acknowledgeNegative?: boolean }, idempotencyKey: string,
): Promise<CounterOrder> =>
  api<CounterOrder>(`${CO}/${uid}/transitions/collect`, {
    method: 'POST', body: input, headers: { 'Idempotency-Key': idempotencyKey },
  });

export interface CustomerMatch { uid: string; name: string; phone: string | null }

export const findCustomerByPhone = (q: string): Promise<{ items: CustomerMatch[] }> =>
  api<{ items: CustomerMatch[] }>(`/customers?q=${encodeURIComponent(q)}`);
